import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, BusyIndicator, CheckBox, Title } from "@ui5/webcomponents-react";
import { toast } from "sonner";
import { AdminApi } from "../../api/client";

// First field error from a 422, else the server message, else a fallback.
const apiError = (e, fallback) => {
  const data = e?.response?.data;
  if (data?.fields) return Object.values(data.fields)[0];
  return data?.message || fallback;
};

// Local calendar date (NOT toISOString, which is UTC and is off-by-one near midnight in CET/CEST).
const pad2 = (n) => String(n).padStart(2, "0");
const isoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const WEEKDAYS = [
  { n: 1, l: "Mo" }, { n: 2, l: "Di" }, { n: 3, l: "Mi" }, { n: 4, l: "Do" },
  { n: 5, l: "Fr" }, { n: 6, l: "Sa" }, { n: 7, l: "So" },
];

function Field({ label, children }) {
  return (
    <div className="field-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

export default function SlotsPage() {
  const today = new Date();
  const in30 = new Date(Date.now() + 30 * 864e5);

  const [from, setFrom] = useState(isoDate(today));
  const [to, setTo] = useState(isoDate(in30));
  const [slots, setSlots] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDur, setBulkDur] = useState(30);

  const [single, setSingle] = useState({ date: isoDate(in30), time: "10:00", durationMinutes: 30 });
  const [series, setSeries] = useState({
    startDate: isoDate(today), endDate: isoDate(in30), weekdays: [1, 2, 3, 4, 5],
    dailyStart: "09:00", dailyEnd: "12:00", durationMinutes: 30, breakMinutes: 0,
  });

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    setSlots(null);
    setSelected(new Set());
    AdminApi.listSlots(from, to)
      .then((r) => { if (id === reqId.current) setSlots(r); })
      .catch(() => { if (id === reqId.current) setSlots([]); });
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const createSingle = async () => {
    try {
      const r = await AdminApi.createSlot({ startsAt: `${single.date}T${single.time}`, durationMinutes: Number(single.durationMinutes) });
      toast.success(r.created ? "Slot angelegt." : "Slot übersprungen (Überschneidung/Vergangenheit).");
      load();
    } catch (e) {
      toast.error(apiError(e, "Slot konnte nicht angelegt werden."));
    }
  };

  const createSeries = async () => {
    if (series.weekdays.length === 0) { toast.error("Bitte mindestens einen Wochentag wählen."); return; }
    try {
      const r = await AdminApi.createSeries({ ...series, durationMinutes: Number(series.durationMinutes), breakMinutes: Number(series.breakMinutes) });
      toast.success(`${r.created} Slots angelegt, ${r.skippedOverlap} übersprungen${r.capped ? " (Limit erreicht)" : ""}.`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Serie konnte nicht angelegt werden."));
    }
  };

  const remove = async (slot) => {
    try {
      await AdminApi.deleteSlot(slot.id);
      toast.success("Slot gelöscht.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen."));
    }
  };

  const toggleWeekday = (n) =>
    setSeries((s) => ({ ...s, weekdays: s.weekdays.includes(n) ? s.weekdays.filter((x) => x !== n) : [...s.weekdays, n].sort() }));

  // Only un-booked slots can be selected/bulk-deleted (booked ones are skipped server-side anyway).
  const deletable = (slots || []).filter((s) => s.status !== "booked");
  const allSelected = deletable.length > 0 && deletable.every((s) => selected.has(s.id));

  const toggleSelect = (id) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(deletable.map((s) => s.id)));

  const runBulk = async (payload, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const r = await AdminApi.bulkDeleteSlots(payload);
      toast.success(`${r.deleted} Slot(s) gelöscht${r.skipped ? `, ${r.skipped} übersprungen (gebucht)` : ""}.`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen."));
    } finally {
      setBulkBusy(false);
    }
  };

  const changeDurationSelected = async () => {
    if (selected.size === 0) { toast.error("Bitte zuerst Slots auswählen."); return; }
    if (!window.confirm(`${selected.size} ausgewählte(n) Slot(s) auf ${bulkDur} Min setzen? Überschneidungen werden übersprungen.`)) return;
    setBulkBusy(true);
    try {
      const r = await AdminApi.bulkDurationSlots({ ids: [...selected], durationMinutes: bulkDur });
      toast.success(`${r.updated} Slot(s) geändert${r.skipped ? `, ${r.skipped} übersprungen (Überschneidung)` : ""}.`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(apiError(e, "Änderung fehlgeschlagen."));
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteSelected = () =>
    runBulk({ ids: [...selected] }, `${selected.size} ausgewählte(n) Slot(s) wirklich löschen?`);
  const deleteRange = () =>
    runBulk({ from, to }, `Wirklich ALLE freien Slots im Zeitraum ${from} bis ${to} löschen? (Gebuchte bleiben erhalten.)`);

  return (
    <div>
      <Title level="H2" style={{ margin: "8px 0 16px" }}>Slots verwalten</Title>

      <div className="card">
        <h3>Einzelnen Slot anlegen</h3>
        <div className="form-grid">
          <Field label="Datum"><input className="native-input" type="date" value={single.date} onChange={(e) => setSingle({ ...single, date: e.target.value })} /></Field>
          <Field label="Uhrzeit"><input className="native-input" type="time" value={single.time} onChange={(e) => setSingle({ ...single, time: e.target.value })} /></Field>
          <Field label="Dauer (Min)"><input className="native-input" type="number" min="5" step="5" value={single.durationMinutes} onChange={(e) => setSingle({ ...single, durationMinutes: e.target.value })} /></Field>
          <Button design="Emphasized" icon="add" onClick={createSingle}>Anlegen</Button>
        </div>
      </div>

      <div className="card">
        <h3>Serie anlegen (z.B. Mo–Fr 09–12 Uhr)</h3>
        <div className="form-grid">
          <Field label="Von"><input className="native-input" type="date" value={series.startDate} onChange={(e) => setSeries({ ...series, startDate: e.target.value })} /></Field>
          <Field label="Bis"><input className="native-input" type="date" value={series.endDate} onChange={(e) => setSeries({ ...series, endDate: e.target.value })} /></Field>
          <Field label="Beginn/Tag"><input className="native-input" type="time" value={series.dailyStart} onChange={(e) => setSeries({ ...series, dailyStart: e.target.value })} /></Field>
          <Field label="Ende/Tag"><input className="native-input" type="time" value={series.dailyEnd} onChange={(e) => setSeries({ ...series, dailyEnd: e.target.value })} /></Field>
          <Field label="Dauer (Min)"><input className="native-input" type="number" min="5" step="5" value={series.durationMinutes} onChange={(e) => setSeries({ ...series, durationMinutes: e.target.value })} /></Field>
          <Field label="Pause (Min)"><input className="native-input" type="number" min="0" step="5" value={series.breakMinutes} onChange={(e) => setSeries({ ...series, breakMinutes: e.target.value })} /></Field>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#4a5867", fontWeight: 600 }}>Wochentage:</span>
          {WEEKDAYS.map((w) => (
            <CheckBox key={w.n} text={w.l} checked={series.weekdays.includes(w.n)} onChange={() => toggleWeekday(w.n)} />
          ))}
          <Button design="Emphasized" icon="add" onClick={createSeries}>Serie anlegen</Button>
        </div>
      </div>

      <div className="toolbar-row">
        <Field label="Anzeigen von"><input className="native-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="bis"><input className="native-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button icon="refresh" onClick={load}>Aktualisieren</Button>
        <Button design="Negative" icon="delete" disabled={selected.size === 0 || bulkBusy} onClick={deleteSelected}>
          Ausgewählte löschen{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
        <Button design="Transparent" icon="sys-cancel" disabled={bulkBusy || !(slots && slots.length)} onClick={deleteRange}>
          Alle freien im Zeitraum löschen
        </Button>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input className="native-input" type="number" min="5" step="5" value={bulkDur} style={{ width: 80 }}
            onChange={(e) => setBulkDur(Math.max(5, Number(e.target.value) || 0))} />
          <Button design="Transparent" icon="duration" disabled={selected.size === 0 || bulkBusy} onClick={changeDurationSelected}>
            Dauer der Ausgewählten ändern{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
        </span>
      </div>

      {slots === null ? (
        <div className="center-screen"><BusyIndicator active /></div>
      ) : slots.length === 0 ? (
        <p className="muted">Keine Slots in diesem Zeitraum.</p>
      ) : (
        <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" aria-label="Alle freien auswählen" checked={allSelected} onChange={toggleSelectAll} />
              </th>
              <th>Datum</th><th>Zeit</th><th>Dauer</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {slots.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.status !== "booked" && (
                    <input type="checkbox" aria-label={`Slot ${s.date} ${s.time}`} checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} />
                  )}
                </td>
                <td>{s.date}</td>
                <td>{s.time}</td>
                <td>{s.durationMinutes} Min</td>
                <td><span className={`badge ${s.status}`}>{s.status}</span></td>
                <td style={{ textAlign: "right" }}>
                  {s.status !== "booked" && (
                    <Button design="Transparent" icon="delete" onClick={() => remove(s)} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
